import {
  flip,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { useState } from "react";
import css from "./Chart.module.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { priceFormat } from "../item/Price";
import { useUrlQuery } from "~/util/UrlUtils";
import { z } from "zod";

dayjs.extend(utc);

const PageQuery = z.object({
  period: z.optional(z.string()),
});

export function Chart(params: {
  data: { date: Date; count: number; }[];
  title: string;
  range: "24h";
  color?: string;
  prefix?: string;
  timezone: string;
}) {
  const { data, range, title, timezone } = params;
  const urlQuery = useUrlQuery();
  const query = PageQuery.parse(urlQuery);

  const arr = generate({ data, period: query.period, timezone });
  const color = params.color ? params.color : "#fff";
  const prefix = params.prefix ? params.prefix : null;
  const sum = arr.reduce((pSum, a) => pSum + a.count, 0);

  const _period = getPeriod(query.period);
  const diff = Math.round(_period.endDate.diff(_period.startDate, "days", true));
  const xValues = diff === 1 ? ["00:00", "12:00", "23:00"] : [_period.startDate.format("YYYY-MM-DD"), dayjs((_period.startDate.unix() + _period.endDate.unix()) / 2 * 1000).format("YYYY-MM-DD"), _period.endDate.format("YYYY-MM-DD")];

  return (
    <>
      <div className={css.root}>
        <div className={css.title}>{title}</div>
        {/* <pre>{JSON.stringify(arr, null, 2)}</pre> */}
        <div
          className={css.chart}
          style={range === "24h" ? { gap: 4 } : { gap: 15 }}
        >
          {arr.map((x, i) => (
            <Column
              key={i}
              date={x.date}
              count={x.count}
              maxCount={Math.max(...arr.map((x) => x.count))}
              color={color}
              prefix={prefix}
            />
          ))}
        </div>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10
        }}>{xValues.map((date, i) => <div key={i}>{date}</div>)}</div>
        <div className={css.total}>
          <div>{prefix}</div>
          <div>{priceFormat(sum)}</div>
        </div>
      </div>
    </>
  );
}

function Column(params: {
  maxCount: number;
  count: number;
  date: string;
  color: string;
  prefix: string | null;
}) {
  const { count, maxCount, date, color, prefix } = params;
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    middleware: [offset(5), flip(), shift({ padding: 8 })],
    onOpenChange: (newOpen) => {
      setIsOpen(newOpen);
    },
  });

  const hover = useHover(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);
  return (
    <>
      <div
        className={css.bar}
        style={{
          height: calcHeight(maxCount, count),
          background: color,
        }}
        ref={refs.setReference}
        {...getReferenceProps()}
      ></div>
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className={css.tooltip}
        >
          <div>{date}</div>
          <div style={{ color }}>{`${priceFormat(count)} ${prefix}`}</div>
        </div>
      )}
    </>
  );
}

function daysInCurrentMonth() {
  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth() + 1;
  return new Date(currentYear, currentMonth, 0).getDate();
}

function generate(params: {
  data: { count: number; date: Date; }[],
  period: string | undefined;
  timezone: string;
}) {

  const { startDate, endDate } = getPeriod(params.period);

  const diff = Math.round(endDate.diff(startDate, "days", true));

  switch (diff) {
    case 1:
      return day(params.data, params.timezone);
    default:
      return week(params.data, diff, startDate);
  }
}

function day(data: { count: number; date: Date; }[], timezone: string) {
  const date = data.length > 0 ? dayjs(data[0].date) : dayjs();

  let arr: { date: string; count: number; }[] = [];
  for (let i = 0; i < 24; i++) {
    arr.push({ date: date.tz(timezone).format(`YYYY-MM-DD ${i}:00`), count: 0 });
  }
  for (const d of data) {
    const hour = dayjs(d.date).tz(timezone).get("hour");
    arr[hour] = { ...arr[hour], count: parseInt(d.count.toString()) };
  }

  return arr;
}

function week(data: { count: number; date: Date; }[], days: number, startDate: dayjs.Dayjs) {
  let map = new Map<string, { date: string; count: number; }>();
  for (let i = 0; i < days; i++) {
    const strDate = dayjs(startDate).add(i, "day").format(`YYYY-MM-DD`);
    map.set(strDate, { date: strDate, count: 0 });
  }
  for (const d of data) {
    const strDate = dayjs(d.date).format(`YYYY-MM-DD`);
    const date = map.get(strDate);
    if (date) {
      date.count += parseInt(d.count.toString());
    }
  }

  return Array.from(map.values());
}

function calcHeight(maxCount: number, count: number) {
  const calc = count ? Math.round((count / maxCount) * 100) : 3;
  return calc ? calc : 5;
}


function getPeriod(value: string | undefined) {
  const todayDate = dayjs();
  const yesterdayDate = todayDate.subtract(1, "day");
  const lastWeek = todayDate.subtract(6, "day");
  const lastMonth = todayDate.subtract(29, "day");

  let period: string | undefined;
  switch (value) {
    case "today":
      period = `${todayDate.startOf("day").format('YYYY-MM-DD')}:${todayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    case "yesterday":
      period = `${yesterdayDate.startOf("day").format('YYYY-MM-DD')}:${yesterdayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    case "week":
      period = `${lastWeek.startOf("day").format('YYYY-MM-DD')}:${todayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    case "month":
      period = `${lastMonth.startOf("day").format('YYYY-MM-DD')}:${todayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    default:
      period = value;
      break;
  }

  const [strStartDate, strEndDate] = period ? period.split(":", 2) : [];

  const startDate = dayjs(strStartDate).isValid()
    ? dayjs(strStartDate).startOf("day")
    : dayjs().startOf("day");
  const endDate = dayjs(strEndDate).isValid()
    ? dayjs(strEndDate).endOf("day")
    : dayjs().endOf("day");

  return { startDate, endDate };
}